import React, {useMemo} from 'react';
import {BreadcrumbItem} from 'react-bootstrap';
import {useSelector} from 'react-redux';
import {useParams, useLocation} from 'react-router-dom';

/**
 * Custom hook to manage breadcrumb navigation
 * Automatically extracts route params and handles different page contexts
 *
 * @param {Object} options - Optional overrides
 * @param {string} options.catrefid - Category reference ID (optional, auto-detected from route)
 * @param {string} options.subcatrefid - Subcategory reference ID (optional, auto-detected from route)
 * @param {string} options.q - Search query (optional, auto-detected from URL)
 * @param {string} options.pid - Product ID (optional, auto-detected from route)
 * @param {string} options.brandName - Brand name (optional, auto-detected from URL)
 */
export default function useBreadcrumbs(options = {}) {
	// Get route params automatically
	const params = useParams();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);

	// Get categories and subcategories from Redux
	const categories = useSelector(state => state.category?.categories || []);
	const subcategories = useSelector(state => state.category?.subcategories || []);

	// Extract parameters from route or options
	const catrefid = options.catrefid || params.catrefid;
	const subcatrefid = options.subcatrefid || params.subcatrefid;
	const q = options.q || searchParams.get('q') || searchParams.get('search');
	const pid = options.pid || params.pid;
	const brandName = options.brandName || params.brandName || searchParams.get('brand');
	const slug = options.slug || params.slug;

	/**
	 * Find category by refId
	 */
	const findCategory = (refId) => {
		if (!refId || !categories) return null;
		return categories.find(cat =>
			cat.refId === refId ||
			cat.ref === refId ||
			cat.id === refId
		);
	};

	/**
	 * Generate breadcrumbs based on current context
	 */
	const breadcrumbs = useMemo(() => {
		const items = [];

		// Home breadcrumb (always first)
		items.push({
			label: 'HOME',
			href: '/',
			active: false,
		});

		// Selection page (landing or partner page)
		if (slug) {
			// Determine page type from current path
			const isPartnerPage = location.pathname.includes('/partner/');
			const pageType = isPartnerPage ? 'Partner' : 'Page';

			items.push({
				label: pageType,
				href: isPartnerPage ? '/partner' : '/page',
				active: false,
			});
			items.push({
				label: decodeURIComponent(slug).replace(/-/g, ' '),
				href: null,
				active: true,
			});
			return items;
		}

		// Search page
		if (q) {
			items.push({
				label: `Search: "${q}"`,
				href: null,
				active: true,
			});
			return items;
		}

		// Brand page
		if (brandName) {
			items.push({
				label: 'Brands',
				href: '/brands',
				active: false,
			});
			items.push({
				label: decodeURIComponent(brandName).replace(/-/g, ' '),
				href: null,
				active: true,
			});
			return items;
		}

		// Product detail page
		if (pid && !catrefid) {
			items.push({
				label: 'Products',
				href: '/products',
				active: false,
			});
			items.push({
				label: 'Product Details',
				href: null,
				active: true,
			});
			return items;
		}

		// Category page
		if (catrefid) {
			const category = findCategory(catrefid);

			items.push({
				label: catrefid.replace(/-/g, ' '),
				href: '/category/' + (category ? category.refId || category.ref || category.id : catrefid),
				active: !subcatrefid,
			});

			if (subcatrefid) {
				items.push({
					label: subcatrefid.replace(/-/g, ' '),
					href: null,
					active: true,
				});
			}
		}

		// Default if no context
		if (items.length === 1) {
			items.push({
				label: 'Products',
				href: null,
				active: true,
			});
		}

		return items;
	}, [catrefid, subcatrefid, q, pid, brandName, slug, location.pathname, categories, subcategories]);

	/**
	 * Render breadcrumb items as React components with custom styling
	 */
	const renderBreadcrumbs = () => {
		return breadcrumbs.map((item, index) => {
			if (item.active) {
				return (
					<BreadcrumbItem
						key={index}
						active
						style={{
							color: '#9cc54b',
							fontWeight: 600,
						}}
					>
						{item.label}
					</BreadcrumbItem>
				);
			}

			return (
				<BreadcrumbItem
					key={index}
					href={item.href}
					style={{
						color: '#666',
						transition: 'color 0.2s ease',
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.color = '#9cc54b';
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.color = '#666';
					}}
				>
					{item.label}
				</BreadcrumbItem>
			);
		});
	};

	/**
	 * Get page title from breadcrumbs
	 */
	const pageTitle = useMemo(() => {
		if (breadcrumbs.length === 0) return 'Products';
		const lastItem = breadcrumbs[breadcrumbs.length - 1];
		return lastItem.label;
	}, [breadcrumbs]);

	/**
	 * Get structured breadcrumb data for SEO (JSON-LD)
	 */
	const structuredData = useMemo(() => {
		return {
			'@context': 'https://schema.org',
			'@type': 'BreadcrumbList',
			itemListElement: breadcrumbs.map((item, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: item.label,
				item: item.href ? `${window.location.origin}${item.href}` : undefined,
			})),
		};
	}, [breadcrumbs]);

	return {
		breadcrumbs,
		renderBreadcrumbs,
		pageTitle,
		structuredData,
		isLoading: false,
	};
}